import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import Checkbox from "@mui/material/Checkbox"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import FormControlLabel from "@mui/material/FormControlLabel"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import PropTypes from "prop-types"
import { useEffect, useState } from "react"

import Column from "../../../common/components/Column"

const INITIAL_FORM = {
  email: "",
  password: "",
  password_confirm: "",
  name: "",
  is_onebase_staff: true,
  is_active: true,
}

export default function AdminConciergeUserCreateDialog({
  open,
  onClose,
  onCreate,
  saving,
}) {
  const [form, setForm] = useState(INITIAL_FORM)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!open) return
    setForm(INITIAL_FORM)
    setError(null)
  }, [open])

  function handleSubmit(event) {
    event.preventDefault()
    setError(null)
    onCreate(form).catch((err) => {
      const data = err?.response?.data
      if (typeof data === "string") {
        setError(data)
      } else if (data && typeof data === "object") {
        const messages = Object.entries(data).flatMap(([field, value]) => {
          const items = Array.isArray(value) ? value : [value]
          return items.map((item) =>
            field === "non_field_errors" ? String(item) : `${field}: ${item}`
          )
        })
        setError(messages.join(" ") || "Could not create concierge user.")
      } else {
        setError("Could not create concierge user.")
      }
    })
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Create concierge user</DialogTitle>
        <DialogContent>
          <Column gap={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Creates a concierge-only platform account with verified email,
              matching Django admin user creation with concierge flags set.
            </Typography>
            {error ? <Alert severity="error">{error}</Alert> : null}
            <TextField
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, email: e.target.value }))
              }
              size="small"
              fullWidth
              required
              autoComplete="off"
            />
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, name: e.target.value }))
              }
              size="small"
              fullWidth
            />
            <TextField
              label="Password"
              type="password"
              value={form.password}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, password: e.target.value }))
              }
              size="small"
              fullWidth
              required
              autoComplete="new-password"
            />
            <TextField
              label="Confirm password"
              type="password"
              value={form.password_confirm}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  password_confirm: e.target.value,
                }))
              }
              size="small"
              fullWidth
              required
              autoComplete="new-password"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.is_onebase_staff}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      is_onebase_staff: e.target.checked,
                    }))
                  }
                />
              }
              label="OneBase staff"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.is_active}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      is_active: e.target.checked,
                    }))
                  }
                />
              }
              label="Active"
            />
          </Column>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={saving}>
            Create concierge
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

AdminConciergeUserCreateDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  saving: PropTypes.bool,
}
